import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
  Stack,
} from '@mui/material';
import { SVButton, SVForm, SVTextInput } from '../core';
import { useState } from 'react';
import { useApproveDeclineTransactionReview } from '@/hooks/requests/useTransactionReview';
import { useForm } from 'react-hook-form';
import { ApproveDeclineTransactionReviewRequestBody } from '@/types/transactionReview';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';

type TransactionReviewApproveDeclineButtonProps = {
  isApprove: boolean;
  transactionReviewUUID: string;
};

export default function TransactionReviewApproveDeclineButton({
  isApprove = true,
  transactionReviewUUID,
}: TransactionReviewApproveDeclineButtonProps) {
  const [open, setOpen] = useState(false);
  const {
    approveTransactionReview,
    declineTransactionReview,
    isLoadingApproveDeclineTransactionReview,
  } = useApproveDeclineTransactionReview();
  const formMethods = useForm<ApproveDeclineTransactionReviewRequestBody>({
    defaultValues: { id: transactionReviewUUID, dueDiligenceNotes: '' },
    mode: 'onBlur',
  });
  const action = isApprove ? 'Approve' : 'Decline';
  const actionTense = isApprove ? 'approved' : 'declined';

  async function handleApproveDecline(
    data: ApproveDeclineTransactionReviewRequestBody,
  ) {
    try {
      if (isApprove) {
        await approveTransactionReview(data);
      } else {
        await declineTransactionReview(data);
      }
      showToast({
        type: 'success',
        data: {
          content: `Transaction successfully ${actionTense}`,
        },
      });
      setOpen(false);
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <>
      <SVButton
        onClick={() => setOpen(true)}
        variant={isApprove ? 'primary' : 'tertiary'}
        fullWidth>
        {action}
      </SVButton>
      <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
        <SVForm<ApproveDeclineTransactionReviewRequestBody>
          methods={formMethods}
          onSubmit={handleApproveDecline}>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="h6" component="h2">
                Confirm Decision & Add Notes
              </Typography>
              <Typography>
                Please provide due diligence notes explaining why this
                transaction is being {actionTense}. Confirm your decision by
                clicking "Yes, {action}".
              </Typography>
              <SVTextInput
                name="dueDiligenceNotes"
                label="Due Diligence Notes"
                rules={{ required: true }}
                multiline
                multilineRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <SVButton onClick={() => setOpen(false)} variant="tertiary">
              No, Don't {action}
            </SVButton>
            <SVButton
              isLoading={isLoadingApproveDeclineTransactionReview}
              variant={isApprove ? 'success' : 'error'}
              onClick={formMethods.handleSubmit(handleApproveDecline)}>
              Yes, {action}
            </SVButton>
          </DialogActions>
        </SVForm>
      </Dialog>
    </>
  );
}
